'use client';

/**
 * The shared trip's map. It follows the itinerary: the stop you're reading
 * gets the ring and the lift, and the map pans to it if it's off screen.
 * Tapping a pin scrolls the itinerary to that stop.
 *
 * Road lines, best first (sharedTrip.dayPath): the server's routed line for
 * each day, else Google's legs (fetched once by the page, the same call the
 * planner makes), else straight dashed lines. Each day wears its own colour,
 * the same colour as its day label in the list, and the key in the corner
 * says so. The lines draw in, day by day, the first time the map shows.
 *
 * Pins are the planner's pins (planner/roles.ts): same letter, same colour.
 * A loop's start and finish are one plain pin, "A"
 * (the key reads "Start & finish"). Pins hide the base map's labels
 * they would cover, so a town name is never cut in half.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { TripStop } from '../../../types/trip';
import { getGoogleMapsLoader } from '../../../lib/googleMapsLoader';
import { ROLE_HEX, roleAt, stopLetter, stopPinSvg, type StopRole } from '../../../components/planner/roles';
import type { SheetDay } from '../../../utils/tripSheet';
import { dayColorMap, dayPath, type StaticView } from './sharedTrip';
import StaticRouteMap from './StaticRouteMap';

import { DAY_COLORS as DAY_HEX } from '../../../utils/tripDayColors';

// The planner's tp.day-1..7 palette (utils/tripDayColors), counted by driving day.
export { DAY_HEX };

const MAP_ID = process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID || 'f1f81d2941d6d32e';
/** Room around the route when the map frames it. */
export const FIT_PADDING = 48;

type LatLng = { lat: number; lng: number };

export interface PinGroup {
  key: string;
  position: LatLng;
  stopIds: string[];
  letters: string[];
  names: string[];
  role: StopRole;
  /** A loop's start that is also its finish. */
  loop: boolean;
}

/** One pin per place: stops within ~200 m (a loop's start and finish) share it. */
export function groupPins(stops: TripStop[]): PinGroup[] {
  const groups: PinGroup[] = [];
  stops.forEach((s, i) => {
    if (typeof s.lat !== 'number' || typeof s.lng !== 'number') return;
    const near = groups.find(
      (g) => Math.abs(g.position.lat - (s.lat as number)) < 0.002 && Math.abs(g.position.lng - (s.lng as number)) < 0.002
    );
    if (near) {
      near.stopIds.push(s.id);
      near.letters.push(stopLetter(i));
      near.names.push(s.name);
      if (near.role === 'start' && i === stops.length - 1) near.loop = true;
      return;
    }
    groups.push({
      key: s.id,
      position: { lat: s.lat, lng: s.lng },
      stopIds: [s.id],
      letters: [stopLetter(i)],
      names: [s.name],
      role: roleAt(stops, i),
      loop: false,
    });
  });
  return groups;
}

export interface LegendEntry {
  role: StopRole;
  label: string;
}

/** The pin key: only the roles on this map, a loop's start reads "Start & finish". */
export function pinLegend(groups: PinGroup[]): LegendEntry[] {
  const order: StopRole[] = ['start', 'overnight', 'via', 'end'];
  const loop = groups.some((g) => g.loop);
  const present = new Set(groups.map((g) => g.role));
  const words: Record<StopRole, string> = {
    start: loop ? 'Start & finish' : 'Start',
    overnight: 'Overnight',
    via: 'On the way',
    end: 'Finish',
  };
  return order.filter((r) => present.has(r)).map((role) => ({ role, label: words[role] }));
}

function pinNode(g: PinGroup, selected: boolean): HTMLDivElement {
  const el = document.createElement('div');
  el.setAttribute('data-stop-id', g.stopIds[0]);
  el.setAttribute('data-stop-ids', g.stopIds.join(' '));
  el.setAttribute('data-testid', 'trip-map-marker');
  el.style.position = 'relative';
  paintPin(el, g, selected);
  return el;
}

function paintPin(el: HTMLElement, g: PinGroup, selected: boolean) {
  el.setAttribute('data-selected', selected ? 'true' : 'false');
  el.style.transformOrigin = '50% 100%';
  el.style.transition = 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1), filter 220ms';
  el.style.transform = selected ? 'scale(1.25)' : 'scale(1)';
  el.style.filter = selected ? 'drop-shadow(0 6px 10px rgba(22,35,58,0.35))' : 'drop-shadow(0 1px 2px rgba(22,35,58,0.3))';
  el.style.cursor = 'pointer';
  // A loop: one plain pin, its letter (the key says "Start & finish"; a badge
  // on the pin read as a glitch at map scale).
  // Two different stops at one spot (rare): the second letter as a small tag.
  const extra = g.loop ? [] : g.letters.slice(1);
  const badge = extra.length
      ? `<span aria-hidden="true" style="position:absolute;left:24px;top:3px;height:18px;padding:0 6px;border-radius:9px;background:#fff;color:#16233a;font:700 11px/18px 'Work Sans',Arial,sans-serif;box-shadow:0 1px 3px rgba(22,35,58,0.3);white-space:nowrap">${extra.join(' ')}</span>`
      : '';
  el.innerHTML = stopPinSvg(g.letters[0], g.role, { selected }) + badge;
}

export default function SharedTripMap({
  stops,
  days,
  drivePaths,
  legPaths,
  activeStopId,
  onSelectStop,
  className = '',
  staticView = null,
  staticMedia,
  live = true,
}: {
  stops: TripStop[];
  days: SheetDay[];
  /** `${fromId}>${toId}` of a day's drive → the server's routed line. */
  drivePaths: Record<string, LatLng[]>;
  /** `${fromId}>${toId}` of each leg → Google's road line (null while loading). */
  legPaths: Record<string, LatLng[]> | null;
  activeStopId: string | null;
  onSelectStop: (stopId: string) => void;
  className?: string;
  /** The route as a picture, shown until the live map's tiles are drawn. */
  staticView?: StaticView | null;
  /** Media query the picture loads under (see StaticRouteMap). */
  staticMedia?: string;
  /** False: the picture only (server render, or before we know the screen). */
  live?: boolean;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<Map<string, { marker: google.maps.marker.AdvancedMarkerElement; el: HTMLElement; group: PinGroup }>>(new Map());
  const linesRef = useRef<google.maps.Polyline[]>([]);
  const drawnRef = useRef(false);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [tiles, setTiles] = useState(false);
  const selectRef = useRef(onSelectStop);
  selectRef.current = onSelectStop;

  const groups = useMemo(() => groupPins(stops), [stops]);
  const driveDays = useMemo(() => days.filter((d) => d.type === 'drive'), [days]);
  const colors = useMemo(() => dayColorMap(days), [days]);
  const dayHex = (n: number) => colors.get(n) ?? DAY_HEX[0];
  const located = useMemo(
    () => stops.filter((s) => typeof s.lat === 'number' && typeof s.lng === 'number') as (TripStop & LatLng)[],
    [stops]
  );
  const lines = useMemo(
    () => driveDays.map((d) => ({ day: d.dayNumber, ...dayPath(d, drivePaths, legPaths) })),
    [driveDays, drivePaths, legPaths]
  );
  const routedSig = lines.map((l) => `${l.day}:${l.routed ? l.path.length : 0}`).join('|');

  // 1. The map itself.
  useEffect(() => {
    let cancelled = false;
    const host = hostRef.current;
    if (!live || !host || located.length === 0) return;
    (async () => {
      try {
        const loader = getGoogleMapsLoader();
        await Promise.all([loader.importLibrary('maps'), loader.importLibrary('marker')]);
        if (cancelled || !hostRef.current) return;
        const map = new google.maps.Map(hostRef.current, {
          mapId: MAP_ID,
          center: { lat: located[0].lat, lng: located[0].lng },
          zoom: 7,
          // Our own rounded zoom buttons (below), in the page's look.
          disableDefaultUI: true,
          zoomControl: false,
          clickableIcons: false,
          gestureHandling: 'cooperative',
          // Fit the trip snugly instead of snapping out a whole zoom level.
          isFractionalZoomEnabled: true,
          keyboardShortcuts: true,
        });
        mapRef.current = map;
        google.maps.event.addListenerOnce(map, 'tilesloaded', () => !cancelled && setTiles(true));
        setStatus('ready');
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, located.length > 0]);

  // 2. Frame the route itself (the road, not just the pins), snugly.
  useEffect(() => {
    const map = mapRef.current;
    const host = hostRef.current;
    if (status !== 'ready' || !map || !host || !located.length) return;
    const fit = () => {
      const b = new google.maps.LatLngBounds();
      located.forEach((s) => b.extend(s));
      lines.forEach((l) => l.path.forEach((p) => b.extend(p)));
      // 48px all round (the key lives above the map now), plus room at the
      // sides for the town names beside the end pins ("Melbourne" sits to
      // the right of its pin) and the zoom buttons on the right.
      const narrow = host.clientWidth < 520;
      map.fitBounds(b, {
        top: FIT_PADDING + 8,
        bottom: FIT_PADDING,
        left: FIT_PADDING + (narrow ? 16 : 24),
        right: FIT_PADDING + (narrow ? 24 : 48),
      });
    };
    fit();
    // The box is sized to the route once its width is known: frame again then.
    if (typeof ResizeObserver === 'undefined') return;
    let last = `${host.clientWidth}x${host.clientHeight}`;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const ro = new ResizeObserver(() => {
      const size = `${host.clientWidth}x${host.clientHeight}`;
      if (size === last) return;
      last = size;
      clearTimeout(timer);
      timer = setTimeout(fit, 120);
    });
    ro.observe(host);
    return () => {
      ro.disconnect();
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, routedSig, located.length]);

  // 3. Draw each day's line in its colour; the first time, draw it in.
  useEffect(() => {
    const map = mapRef.current;
    if (status !== 'ready' || !map) return;
    linesRef.current.forEach((l) => l.setMap(null));
    linesRef.current = [];
    // Google's roads are on their way: wait for them rather than flash dashes.
    if (legPaths === null && !lines.every((l) => l.routed)) return;
    const drawn: { halo: google.maps.Polyline; line: google.maps.Polyline; path: LatLng[] }[] = [];
    lines.forEach((l) => {
      if (l.path.length < 2) return;
      const color = dayHex(l.day);
      const halo = new google.maps.Polyline({ map, path: l.path, strokeColor: '#ffffff', strokeOpacity: 0.95, strokeWeight: 8, zIndex: 1, clickable: false });
      const line = new google.maps.Polyline({
        map,
        path: l.path,
        strokeColor: color,
        strokeOpacity: l.routed ? 1 : 0,
        strokeWeight: 4.5,
        zIndex: 2,
        clickable: false,
        // An estimate is drawn dashed: it's the idea of the road, not the road.
        icons: l.routed
          ? undefined
          : [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, strokeColor: color, scale: 3.5 }, offset: '0', repeat: '14px' }],
      });
      linesRef.current.push(halo, line);
      drawn.push({ halo, line, path: l.path });
    });
    const reduced = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const allRouted = lines.every((l) => l.routed);
    if (drawnRef.current || reduced || !allRouted || !drawn.length) return;
    drawnRef.current = true;
    // Days draw one after another, ~1.4s for the whole trip.
    const total = drawn.reduce((s, d) => s + d.path.length, 0);
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const shown = Math.min(1, (now - start) / 1400) * total;
      let before = 0;
      drawn.forEach((d) => {
        const n = Math.max(0, Math.min(d.path.length, Math.ceil(shown - before)));
        const slice = n >= d.path.length ? d.path : d.path.slice(0, Math.max(1, n));
        d.halo.setPath(slice);
        d.line.setPath(slice);
        before += d.path.length;
      });
      if (shown < total) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [status, lines, legPaths]);

  // 4. Pins.
  useEffect(() => {
    const map = mapRef.current;
    if (status !== 'ready' || !map) return;
    markersRef.current.forEach(({ marker }) => {
      marker.map = null;
    });
    markersRef.current.clear();
    groups.forEach((g, i) => {
      const el = pinNode(g, false);
      const marker = new google.maps.marker.AdvancedMarkerElement({
        map,
        position: g.position,
        content: el,
        title: g.loop
          ? `${g.letters[0]} · ${g.names[0].split(',')[0]}, start and finish`
          : g.names.map((n, k) => `${g.letters[k]} · ${n}`).join(', '),
        zIndex: 10 + i,
        // Hide the base map's labels under the pin instead of cutting them ("Mell…").
        collisionBehavior: google.maps.CollisionBehavior.REQUIRED_AND_HIDES_OPTIONAL,
      });
      marker.addListener('click', () => selectRef.current(g.stopIds[0]));
      markersRef.current.set(g.key, { marker, el, group: g });
    });
  }, [status, groups]);

  // 5. Follow the reader.
  useEffect(() => {
    const map = mapRef.current;
    if (status !== 'ready' || !map) return;
    let target: LatLng | null = null;
    markersRef.current.forEach(({ marker, el, group }, key) => {
      const on = !!activeStopId && group.stopIds.includes(activeStopId);
      if ((el.getAttribute('data-selected') === 'true') !== on) paintPin(el, group, on);
      marker.zIndex = on ? 1000 : 10 + groups.findIndex((g) => g.key === key);
      if (on) target = group.position;
    });
    if (target) {
      // Nudge, don't recentre: pan just far enough to keep the pin clear of
      // the edges, so the rest of the route stays on screen while you read.
      const t = target as LatLng;
      map.panToBounds(new google.maps.LatLngBounds(t, t), { top: 48, bottom: 48, left: 64, right: 72 });
    }
  }, [status, activeStopId, groups]);

  const zoomBy = (d: number) => {
    const map = mapRef.current;
    const z = map?.getZoom();
    if (map && typeof z === 'number') map.setZoom(Math.round(z) + d);
  };
  const zoomBtn =
    'flex h-10 w-10 min-h-0 min-w-0 items-center justify-center border-0 bg-transparent p-0 text-tp-ink transition-colors duration-150 ease-tp-out hover:bg-tp-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-tp-brand/45 active:bg-tp-line';

  const pictured = !!staticView && (status !== 'ready' || !tiles);
  return (
    <div className={`tp-muted-map relative overflow-hidden bg-[#e8eef5] ${className}`} data-map-ready={tiles ? '1' : '0'} data-testid="trip-map">
      {staticView && (
        <StaticRouteMap
          view={staticView}
          media={staticMedia}
          className={`absolute inset-0 transition-opacity duration-500 ease-tp-out motion-reduce:transition-none ${pictured ? 'opacity-100' : 'opacity-0'}`}
          lines={lines.map((l) => ({ key: String(l.day), color: dayHex(l.day), path: l.path, routed: l.routed }))}
          pins={groups.map((g) => ({ key: g.key, position: g.position, letter: g.letters[0], role: g.role, loop: g.loop }))}
        />
      )}
      <div
        ref={hostRef}
        className={`absolute inset-0 transition-opacity duration-500 ease-tp-out motion-reduce:transition-none ${tiles || !staticView ? 'opacity-100' : 'opacity-0'}`}
        aria-label="Map of the trip"
        role="region"
      />
      {status === 'ready' && tiles && (
        <div
          className="absolute bottom-7 right-3 z-[1] flex animate-tp-fade flex-col overflow-hidden rounded-[12px] bg-white shadow-tp-card ring-1 ring-black/5 motion-reduce:animate-none"
          role="group"
          aria-label="Zoom"
        >
          <button type="button" className={zoomBtn} onClick={() => zoomBy(1)} aria-label="Zoom in" title="Zoom in">
            <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
          <span aria-hidden="true" className="mx-2 h-px bg-tp-line" />
          <button type="button" className={zoomBtn} onClick={() => zoomBy(-1)} aria-label="Zoom out" title="Zoom out">
            <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
              <path d="M5 12h14" />
            </svg>
          </button>
        </div>
      )}
      {status === 'error' ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
          <p className="m-0 rounded-tp bg-white/95 px-4 py-2 text-tp-sm text-tp-muted shadow-tp-card">The live map didn&rsquo;t load. The plan has everything.</p>
        </div>
      ) : !staticView && (status !== 'ready' || !tiles) ? (
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <span className="block h-full w-full animate-tp-pulse bg-[linear-gradient(135deg,#eef3f8_0%,#e3eaf2_100%)] motion-reduce:animate-none" />
        </div>
      ) : null}
      {located.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="m-0 text-tp-sm text-tp-muted">No map for this trip: its stops have no coordinates.</p>
        </div>
      )}
    </div>
  );
}

/**
 * The map's header bar, above the map (never over its labels): each day as a
 * chip in its line colour, which doubles as the day nav (the day in view is
 * lit; a tap scrolls to it), and the pin key on the right.
 */
export function MapHeader({
  days,
  stops,
  activeDay,
  onPickDay,
  className = '',
}: {
  days: SheetDay[];
  stops: TripStop[];
  activeDay: number | null;
  onPickDay?: (day: SheetDay) => void;
  className?: string;
}) {
  const driveDays = days.filter((d) => d.type === 'drive');
  const colors = dayColorMap(days);
  const dayHex = (n: number) => colors.get(n) ?? DAY_HEX[0];
  const legend = pinLegend(groupPins(stops));
  return (
    <div className={`flex min-h-[52px] items-center justify-between gap-3 border-0 border-b border-solid border-tp-line bg-white px-2.5 py-2 ${className}`} data-testid="map-key">
      {driveDays.length > 0 && (
        <ul className="m-0 flex min-w-0 list-none items-center gap-1 overflow-x-auto p-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" style={{ paddingLeft: 0 }} aria-label="Days on the map">
          {driveDays.map((d) => {
            const color = dayHex(d.dayNumber);
            const on = activeDay === d.dayNumber;
            const label = `Day ${d.dayNumber}`;
            const inner = (
              <>
                <span aria-hidden="true" className={`rounded-full transition-[height] duration-200 ease-tp-out ${on ? 'h-1 w-4' : 'h-[3px] w-4'}`} style={{ background: color }} />
                {label}
              </>
            );
            const cls = `inline-flex h-9 min-h-0 flex-none items-center gap-1.5 whitespace-nowrap rounded-full border-0 px-3 text-tp-sm transition-[background-color,color] duration-200 ease-tp-out motion-reduce:transition-none ${
              on ? 'font-bold text-tp-ink' : 'bg-transparent font-semibold text-tp-muted hover:bg-tp-surface hover:text-tp-ink'
            }`;
            return (
              <li key={d.dayNumber}>
                {onPickDay ? (
                  <button
                    type="button"
                    onClick={() => onPickDay(d)}
                    aria-current={on ? 'step' : undefined}
                    aria-label={`${label}: ${d.from.name.split(',')[0]} to ${d.to.name.split(',')[0]}`}
                    className={`${cls} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45`}
                    style={on ? { background: `${color}1a` } : undefined}
                  >
                    {inner}
                  </button>
                ) : (
                  <span className={cls}>{inner}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {legend.length > 1 && (
        <ul className="m-0 hidden flex-none list-none items-center gap-3 p-0 pr-1.5 xl:flex" style={{ paddingLeft: 0 }} aria-label="Pin colours">
          {legend.map((e) => (
            <li key={e.role} className="inline-flex items-center gap-1.5 whitespace-nowrap text-tp-xs text-tp-muted">
              <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full ring-2 ring-white" style={{ background: ROLE_HEX[e.role] }} />
              {e.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
